import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Icon from '../ui/Icon';
import { ApiError } from '../../api/client';
import { requestPasswordReset, verifyResetOtp } from '../../api/auth';
import { formatCountdown, maskEmail } from '../../utils/format';
import { AlertBox, pillButton } from './AuthShell';
import OtpInput from './OtpInput';
import useCountdown from './useCountdown';

const LENGTH = 6;
const emptyCode = () => Array(LENGTH).fill('');

/**
 * Step 2: enter the 6-digit code. Shows the code's remaining time, a throttled
 * "Resend OTP" (the server silently ignores early resends, so the countdown matters),
 * and handles wrong (attemptsLeft), expired and locked (429) codes.
 */
export default function VerifyOtpStep({ email, codeExpiresAt, resendAt, onVerified, onResent, onChangeEmail }) {
  const [digits, setDigits] = useState(emptyCode);
  const [error, setError] = useState(null); // { message, kind: 'wrong' | 'expired' | 'locked' | 'other' }
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [locked, setLocked] = useState(false);
  const otpRef = useRef(null);
  const resendRef = useRef(null);
  const alertRef = useRef(null);
  const busy = useRef(false);
  // Bumped to move focus to the first box; applied once the boxes are enabled again
  const [focusRequest, setFocusRequest] = useState(0);

  const codeSeconds = useCountdown(codeExpiresAt);
  const resendSeconds = useCountdown(resendAt);
  const expired = codeSeconds === 0;
  const highlightResend = expired || locked || error?.kind === 'expired';

  useEffect(() => otpRef.current?.focus(0), []);

  useEffect(() => {
    if (focusRequest && !verifying && !resending) otpRef.current?.focus(0);
  }, [focusRequest, verifying, resending]);

  // Point at "Resend OTP" when it's usable; otherwise at the message (a disabled button can't take focus)
  const focusResendOrMessage = () => {
    requestAnimationFrame(() => {
      if (resendRef.current && !resendRef.current.disabled) resendRef.current.focus();
      else alertRef.current?.focus();
    });
  };

  // Code ran out: point the admin at "Resend OTP"
  useEffect(() => {
    if (expired) focusResendOrMessage();
  }, [expired]);

  const verify = async (code = digits.join('')) => {
    if (busy.current || locked || expired) return;
    if (!/^\d{6}$/.test(code)) {
      setError({ message: 'Enter the 6-digit code from the email.', kind: 'other' });
      otpRef.current?.focus();
      return;
    }
    busy.current = true;
    setVerifying(true);
    setError(null);
    try {
      const result = await verifyResetOtp(email, code);
      onVerified(result);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
      const attemptsLeft = err?.data?.Data?.attemptsLeft;
      if (err?.status === 429) {
        setLocked(true);
        setError({ message, kind: 'locked' });
        focusResendOrMessage();
      } else if (err?.status === 400 && typeof attemptsLeft === 'number') {
        setError({ message, kind: 'wrong' });
        setDigits(emptyCode());
        setFocusRequest((n) => n + 1);
      } else if (err?.status === 400) {
        setError({ message, kind: 'expired' });
        focusResendOrMessage();
      } else {
        setError({ message, kind: 'other' });
      }
    } finally {
      busy.current = false;
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (resending || resendSeconds > 0) return;
    setResending(true);
    setError(null);
    try {
      const result = await requestPasswordReset(email);
      setDigits(emptyCode());
      setLocked(false);
      onResent(result);
      toast.success(result.message || 'A new code has been sent.');
      setFocusRequest((n) => n + 1);
    } catch (err) {
      setError({ message: err instanceof ApiError ? err.message : 'Something went wrong. Please try again.', kind: 'other' });
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-secondary-container/30 border border-status-ok-border text-secondary flex items-center justify-center">
        <Icon name="mark_email_read" size={30} />
      </div>
      <h1 className="text-2xl font-bold text-primary text-center mb-2.5">Verify Your Email</h1>
      <p className="text-[13.5px] leading-relaxed text-on-surface-variant text-center px-1 mb-6">
        We sent a 6-digit confirmation code to <strong className="font-semibold text-on-surface">{maskEmail(email)}</strong>.
      </p>

      <form
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          verify();
        }}
      >
        {error && (
          <AlertBox ref={alertRef} id="otp-error" tone={error.kind === 'expired' ? 'pending' : 'alert'}>
            {error.message}
          </AlertBox>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span id="otp-label" className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">
              Security code
            </span>
            <span className={`text-xs font-data-mono ${expired ? 'text-error font-bold' : 'text-on-surface-variant'}`} aria-live="polite">
              {expired ? 'Code expired' : `Expires in ${formatCountdown(codeSeconds)}`}
            </span>
          </div>
          <OtpInput
            ref={otpRef}
            value={digits}
            onChange={(next) => {
              setDigits(next);
              if (error?.kind === 'wrong' || error?.kind === 'other') setError(null);
            }}
            onComplete={(code) => verify(code)}
            disabled={verifying || locked || expired}
            invalid={error?.kind === 'wrong'}
            describedBy={error ? 'otp-error' : undefined}
          />
        </div>

        <button type="submit" disabled={verifying || locked || expired} className={pillButton}>
          {verifying ? (
            <>
              <Icon name="progress_activity" size={20} className="animate-spin" />
              Verifying…
            </>
          ) : (
            <>
              Verify &amp; Continue
              <Icon name="arrow_forward" size={20} />
            </>
          )}
        </button>

        <div className="text-center text-[13px] text-on-surface-variant space-y-2">
          <p>Didn&apos;t receive the email?</p>
          <button
            ref={resendRef}
            type="button"
            onClick={resend}
            disabled={resending || resendSeconds > 0}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot focus-visible:ring-offset-2 disabled:cursor-not-allowed ${
              resendSeconds > 0
                ? 'bg-surface-container text-on-surface-variant'
                : highlightResend
                  ? 'bg-secondary-container text-on-secondary-container ring-2 ring-secondary animate-pulse'
                  : 'bg-secondary-container/70 text-on-secondary-container hover:bg-secondary-container'
            }`}
          >
            <Icon name="refresh" size={16} className={resending ? 'animate-spin' : ''} />
            {resending ? 'Sending…' : resendSeconds > 0 ? `Resend in ${formatCountdown(resendSeconds)}` : 'Resend OTP'}
          </button>
          <div>
            <button
              type="button"
              onClick={onChangeEmail}
              className="text-xs font-semibold text-secondary hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              Change email
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
